import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Code, Book, Lock, ChevronRight, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CodeBlock = ({ code, language }) => (
  <div className="relative">
    <div className="absolute top-2 right-2 text-xs px-2 py-1 rounded bg-gray-700 text-gray-300">
      {language}
    </div>
    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
      <code>{code}</code>
    </pre>
  </div>
);

const APIDocumentation = () => {
  const navigate = useNavigate();
  const [apis, setApis] = useState([]);
  const [selectedApi, setSelectedApi] = useState(null);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchAPIs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/apis`);
      setApis(response.data);
      if (response.data.length > 0) {
        setSelectedApi(response.data[0]);
        if (response.data[0].endpoints?.length > 0) {
          setSelectedEndpoint(response.data[0].endpoints[0]);
        }
      }
    } catch (error) {
      toast.error('Errore nel caricamento delle API');
    } finally {
      setLoading(false);
    }
  }, []);

  const checkAuthentication = useCallback(async () => {
    const token = localStorage.getItem('dev_token');

    if (!token) {
      toast.error('Devi effettuare il login per accedere alla documentazione');
      navigate('/developer-auth');
      return;
    }

    try {
      const response = await axios.get(`${API}/developers/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.is_approved) {
        setIsAuthenticated(true);
        fetchAPIs();
      } else {
        toast.error('Account in attesa di approvazione');
        navigate('/developers');
      }
    } catch (error) {
      toast.error('Sessione scaduta, effettua nuovamente il login');
      localStorage.removeItem('dev_token');
      navigate('/developer-auth');
    }
  }, [navigate, fetchAPIs]);

  useEffect(() => {
    checkAuthentication();
  }, [checkAuthentication]);

  const filteredApis = apis.filter(api =>
    api.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    api.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getMethodColor = (method) => {
    const colors = {
      'GET': 'bg-blue-100 text-blue-700',
      'POST': 'bg-green-100 text-green-700',
      'PUT': 'bg-yellow-100 text-yellow-700',
      'PATCH': 'bg-purple-100 text-purple-700',
      'DELETE': 'bg-red-100 text-red-700'
    };
    return colors[method] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--accent-primary)' }}></div>
          <p style={{ color: 'var(--text-secondary)' }}>Caricamento documentazione...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      {/* Header */}
      <nav className="nav-bar border-b">
        <div className="flex items-center gap-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_ship-smart-6/artifacts/4h2rj0xf_logoonly.png" 
            alt="SpedireOnline.net" 
            className="h-8 w-8 object-contain cursor-pointer"
            onClick={() => navigate('/')}
          />
          <span className="font-bold text-xl cursor-pointer" style={{ color: 'var(--brand-primary)' }} onClick={() => navigate('/')}>
            spedireonline.net
          </span>
          <span className="text-sm px-3 py-1 rounded-full ml-2" style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)' }}>
            Documentazione API
          </span>
        </div>
        <div className="flex items-center gap-4">
          <Button onClick={() => navigate('/developers')} variant="outline" size="sm">
            Info Sviluppatori
          </Button>
          <Button onClick={() => navigate('/developer-dashboard')} variant="outline" size="sm">
            Dashboard
          </Button>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-80 border-r min-h-[calc(100vh-64px)] p-6" style={{ background: 'var(--bg-section)' }}>
          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-muted)' }} />
              <input
                type="text"
                placeholder="Cerca API..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
                style={{ background: 'var(--bg-primary)' }}
              />
            </div>
          </div>

          {/* APIs List */}
          <div className="space-y-2">
            <h3 className="text-xs font-semibold uppercase mb-3" style={{ color: 'var(--text-muted)' }}>
              API Disponibili
            </h3>
            {filteredApis.length === 0 ? (
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                Nessuna API trovata
              </p>
            ) : (
              filteredApis.map((api) => (
                <div key={api.id} className="mb-4">
                  <button
                    onClick={() => {
                      setSelectedApi(api);
                      if (api.endpoints?.length > 0) {
                        setSelectedEndpoint(api.endpoints[0]);
                      } else {
                        setSelectedEndpoint(null);
                      }
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      selectedApi?.id === api.id
                        ? 'bg-white shadow-sm'
                        : 'hover:bg-white hover:bg-opacity-50'
                    }`}
                  >
                    <div className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                      {api.name}
                    </div>
                    <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                      {api.endpoints?.length || 0} endpoints
                    </div>
                  </button>

                  {/* Endpoints submenu */}
                  {selectedApi?.id === api.id && api.endpoints && api.endpoints.length > 0 && (
                    <div className="mt-2 ml-4 space-y-1">
                      {api.endpoints.map((endpoint) => (
                        <button
                          key={endpoint.id}
                          onClick={() => setSelectedEndpoint(endpoint)}
                          className={`w-full text-left px-3 py-2 rounded text-xs flex items-center gap-2 transition-colors ${
                            selectedEndpoint?.id === endpoint.id
                              ? 'bg-white shadow-sm'
                              : 'hover:bg-white hover:bg-opacity-30'
                          }`}
                        >
                          <span className={`px-1.5 py-0.5 rounded font-mono text-xs ${getMethodColor(endpoint.method)}`}>
                            {endpoint.method}
                          </span>
                          <span className="truncate" style={{ color: 'var(--text-secondary)' }}>
                            {endpoint.path}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8 max-w-5xl">
          {!selectedApi ? (
            <div className="text-center py-20">
              <Book className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
              <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                Seleziona un'API
              </h2>
              <p style={{ color: 'var(--text-secondary)' }}>
                Scegli un'API dalla sidebar per visualizzare la documentazione
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* API Header */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 rounded-lg" style={{ background: 'var(--accent-wash)' }}>
                    <Code className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
                      {selectedApi.name}
                    </h1>
                    <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
                      Versione {selectedApi.version} • {selectedApi.category}
                    </p>
                  </div>
                </div>
                <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
                  {selectedApi.description}
                </p>
                {selectedApi.base_url && (
                  <div className="mt-4 p-4 rounded-lg" style={{ background: 'var(--bg-section)' }}>
                    <p className="text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                      Base URL:
                    </p>
                    <code className="text-sm px-3 py-1 rounded" style={{ background: 'var(--bg-primary)', color: 'var(--accent-primary)' }}>
                      {selectedApi.base_url}
                    </code>
                  </div>
                )}
              </div>

              {/* Endpoints */}
              {selectedApi.endpoints && selectedApi.endpoints.length > 0 ? (
                <div>
                  <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                    Endpoints
                  </h2>

                  {selectedEndpoint && (
                    <Card className="mb-8">
                      <CardHeader className="border-b">
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded font-mono text-sm font-bold ${getMethodColor(selectedEndpoint.method)}`}>
                            {selectedEndpoint.method}
                          </span>
                          <code className="text-lg" style={{ color: 'var(--text-primary)' }}>
                            {selectedEndpoint.path}
                          </code>
                        </div>
                        <p className="text-base mt-3" style={{ color: 'var(--text-secondary)' }}>
                          {selectedEndpoint.summary}
                        </p>
                      </CardHeader>
                      <CardContent className="pt-6">
                        {/* Description */}
                        {selectedEndpoint.description && (
                          <div className="mb-6">
                            <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                              Descrizione
                            </h3>
                            <p style={{ color: 'var(--text-secondary)' }}>
                              {selectedEndpoint.description}
                            </p>
                          </div>
                        )}

                        {/* Parameters */}
                        {selectedEndpoint.parameters && selectedEndpoint.parameters.length > 0 && (
                          <div className="mb-6">
                            <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                              Parametri
                            </h3>
                            <div className="space-y-3">
                              {selectedEndpoint.parameters.map((param, idx) => (
                                <div key={param.name || idx} className="border rounded-lg p-4" style={{ background: 'var(--bg-section)' }}>
                                  <div className="flex items-center gap-2 mb-2">
                                    <code className="font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>
                                      {param.name}
                                    </code>
                                    <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                                      {param.type}
                                    </span>
                                    <span className="text-xs px-2 py-0.5 rounded bg-gray-100">
                                      {param.data_type}
                                    </span>
                                    {param.required && (
                                      <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">
                                        required
                                      </span>
                                    )}
                                  </div>
                                  {param.description && (
                                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                                      {param.description}
                                    </p>
                                  )}
                                  {param.example && (
                                    <p className="text-xs mt-2 font-mono" style={{ color: 'var(--text-muted)' }}>
                                      Esempio: <code>{param.example}</code>
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Request Example */}
                        {selectedEndpoint.request_example && (
                          <div className="mb-6">
                            <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                              Esempio Richiesta
                            </h3>
                            <CodeBlock code={selectedEndpoint.request_example} language="JSON" />
                          </div>
                        )}

                        {/* Response Example */}
                        {selectedEndpoint.response_example && (
                          <div className="mb-6">
                            <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                              Esempio Risposta
                            </h3>
                            <CodeBlock code={selectedEndpoint.response_example} language="JSON" />
                          </div>
                        )}

                        {/* Error Codes */}
                        {selectedEndpoint.error_codes && (
                          <div>
                            <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                              Codici di Errore
                            </h3>
                            <pre className="p-4 rounded-lg text-sm" style={{ background: 'var(--bg-section)', color: 'var(--text-secondary)' }}>
                              {selectedEndpoint.error_codes}
                            </pre>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 border rounded-lg" style={{ background: 'var(--bg-section)' }}>
                  <Lock className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                  <p style={{ color: 'var(--text-secondary)' }}>
                    Nessun endpoint configurato per questa API
                  </p>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default APIDocumentation;
